import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'
import { gsap, prefersReducedMotion } from '@/lib/gsap'

interface MagneticProps {
    children: ReactNode
    strength?: number
    className?: string
}

// Buttons drift a little toward the pointer, on devices with a precise pointer only
const Magnetic: FC<MagneticProps> = ({ children, strength = 0.3, className }): ReactNode => {
    const ref = useRef<HTMLSpanElement>(null)

    useEffect(() => {
        const element = ref.current
        if (!element) return
        if (prefersReducedMotion() || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return

        const moveX = gsap.quickTo(element, 'x', { duration: 0.6, ease: 'power3.out' })
        const moveY = gsap.quickTo(element, 'y', { duration: 0.6, ease: 'power3.out' })

        const handleMove = (event: PointerEvent): void => {
            const bounds = element.getBoundingClientRect()
            moveX((event.clientX - (bounds.left + bounds.width / 2)) * strength)
            moveY((event.clientY - (bounds.top + bounds.height / 2)) * strength)
        }

        const handleLeave = (): void => {
            moveX(0)
            moveY(0)
        }

        element.addEventListener('pointermove', handleMove)
        element.addEventListener('pointerleave', handleLeave)

        return () => {
            element.removeEventListener('pointermove', handleMove)
            element.removeEventListener('pointerleave', handleLeave)
            gsap.killTweensOf(element)
            gsap.set(element, { clearProps: 'transform' })
        }
    }, [strength])

    return (
        <span ref={ref} className={cn('inline-flex', className)}>
            {children}
        </span>
    )
}

export default Magnetic
